import React from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function SectionTitle({ num, title }) {
  return (
    <div className="reg-section-title">
      <span className="reg-section-num">{num}</span>
      <span>{title}</span>
    </div>
  );
}

/**
 * Shared patient registration fields (same layout as PatientRegister).
 * variant: admin-add | admin-edit (no OTP; password only on admin-add)
 */
function PatientRegistrationFormFields({
  variant = 'admin-add',
  formData,
  onChange,
  onPincodeBlur,
  locationName = '',
  pincodeError = '',
  isValidatingPincode = false,
  mobileError = '',
  emailError = '',
  isValidatingContact = false,
  onMobileBlur,
  onEmailBlur,
  showPassword = false,
  showReenterPassword = false,
  onToggleShowPassword,
  onToggleShowReenterPassword,
  passwordsMatch = false,
}) {
  const isEdit = variant === 'admin-edit';
  const addressCount = (formData.address || '').length;

  return (
    <>
      <div className="reg-form-section">
        <SectionTitle num={1} title="Personal Information" />
        <div className="row">
          <div className="col-12 p-2">
            <label htmlFor="admin-fullName" className="form-label">
              Full Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="admin-fullName"
              name="fullName"
              value={formData.fullName}
              onChange={onChange}
              placeholder="Enter full name"
              maxLength="100"
              required
            />
          </div>
          <div className="col-md-6 p-2">
            <label htmlFor="admin-age" className="form-label">
              Age <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              id="admin-age"
              name="age"
              value={formData.age}
              onChange={onChange}
              placeholder="e.g. 35"
              required
            />
          </div>
          <div className="col-md-6 p-2">
            <label htmlFor="admin-gender" className="form-label">
              Gender <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              id="admin-gender"
              name="gender"
              value={formData.gender}
              onChange={onChange}
              required
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
          </div>
        </div>
      </div>

      <div className="reg-form-section">
        <SectionTitle num={2} title="Address Details" />
        <div className="row">
          <div className="col-12 p-2">
            <label htmlFor="admin-address" className="form-label">
              Address <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              id="admin-address"
              name="address"
              rows="3"
              value={formData.address}
              onChange={onChange}
              placeholder="House / Flat No., Street, Locality..."
              maxLength="200"
              required
            />
            <small className="form-text text-muted d-block text-end">
              {addressCount}/200 characters
            </small>
          </div>
          <div className="col-md-6 p-2">
            <label htmlFor="admin-landmark" className="form-label">
              Landmark
            </label>
            <input
              type="text"
              className="form-control"
              id="admin-landmark"
              name="landmark"
              value={formData.landmark}
              onChange={onChange}
              placeholder="Nearby school, hospital, temple..."
              maxLength="100"
            />
            <small className="form-text text-muted">
              Optional – helps doctors locate the patient easily.
            </small>
          </div>
          <div className="col-md-6 p-2">
            <label htmlFor="admin-pincode" className="form-label">
              PIN Code <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control w-25"
                id="admin-pincode"
                name="pincode"
                value={formData.pincode}
                onChange={onChange}
                onBlur={onPincodeBlur}
                placeholder="e.g. 695001"
                maxLength="6"
                pattern="[0-9]{6}"
                required
              />
              <input
                type="text"
                className="form-control w-75 form-control-display-only"
                value={locationName}
                placeholder="District / Location"
                readOnly
                tabIndex={-1}
                aria-readonly="true"
              />
            </div>
            {pincodeError && <small className="form-text text-danger">{pincodeError}</small>}
            {isValidatingPincode && (
              <small className="form-text text-muted">Validating pincode...</small>
            )}
          </div>
        </div>
      </div>

      <div className="reg-form-section">
        <SectionTitle num={3} title="Medical Details" />
        <div className="row">
          <div className="col-md-6 p-2">
            <label htmlFor="admin-emergencyName" className="form-label">
              Emergency Contact Name
            </label>
            <input
              type="text"
              className="form-control"
              id="admin-emergencyName"
              name="emergencyName"
              value={formData.emergencyName}
              onChange={onChange}
              placeholder="Full name of emergency contact"
              maxLength="100"
            />
          </div>
          <div className="col-md-6 p-2">
            <label htmlFor="admin-emergencyPhone" className="form-label">
              Emergency Contact Phone
            </label>
            <input
              type="tel"
              className="form-control"
              id="admin-emergencyPhone"
              name="emergencyPhone"
              value={formData.emergencyPhone}
              onChange={onChange}
              placeholder="Mobile number"
              maxLength="10"
            />
          </div>
          <div className="col-12 p-2">
            <label htmlFor="admin-allergies" className="form-label">
              Known Allergies
            </label>
            <textarea
              className="form-control"
              id="admin-allergies"
              name="allergies"
              rows="2"
              value={formData.allergies}
              onChange={onChange}
              placeholder="e.g. Penicillin, Aspirin... (write None if not applicable)"
            />
            <small className="form-text text-muted">
              List any drug, food, or environmental allergies.
            </small>
          </div>
          <div className="col-12 p-2">
            <label htmlFor="admin-medicalHistory" className="form-label">
              Medical History
            </label>
            <textarea
              className="form-control"
              id="admin-medicalHistory"
              name="medicalHistory"
              rows="3"
              value={formData.medicalHistory}
              onChange={onChange}
              placeholder="e.g. Diabetes, Hypertension... (write None if not applicable)"
            />
            <small className="form-text text-muted">
              Include past illnesses, surgeries, hospitalizations, or chronic conditions.
            </small>
          </div>
          <div className="col-12 p-2">
            <label htmlFor="admin-currentMedications" className="form-label">
              Current Medications
            </label>
            <textarea
              className="form-control"
              id="admin-currentMedications"
              name="currentMedications"
              rows="3"
              value={formData.currentMedications}
              onChange={onChange}
              placeholder="e.g. Metformin 500mg... (write None if not applicable)"
            />
            <small className="form-text text-muted">
              List all medicines currently being taken including dosage if known.
            </small>
          </div>
        </div>
      </div>

      <div className="reg-form-section">
        <SectionTitle
          num={4}
          title={isEdit ? 'Contact Details' : 'Contact Details'}
        />
        <div className="row">
          <div className="col-md-6 p-2">
            <label htmlFor="admin-mobileNumber" className="form-label">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <input
              type="tel"
              className={`form-control${isEdit ? ' form-control-display-only' : ''}`}
              id="admin-mobileNumber"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={isEdit ? undefined : onChange}
              onBlur={isEdit ? undefined : onMobileBlur}
              placeholder="9876543210"
              maxLength="10"
              pattern="[0-9]{10}"
              readOnly={isEdit}
              tabIndex={isEdit ? -1 : undefined}
              aria-readonly={isEdit ? 'true' : undefined}
              required={!isEdit}
            />
            {!isEdit && mobileError && (
              <small className="form-text text-danger">{mobileError}</small>
            )}
          </div>
          <div className="col-md-6 p-2">
            <label htmlFor="admin-emailId" className="form-label">
              Email Address <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              className={`form-control${isEdit ? ' form-control-display-only' : ''}`}
              id="admin-emailId"
              name="emailId"
              value={formData.emailId}
              onChange={isEdit ? undefined : onChange}
              onBlur={isEdit ? undefined : onEmailBlur}
              placeholder="patient@email.com"
              maxLength="50"
              readOnly={isEdit}
              tabIndex={isEdit ? -1 : undefined}
              aria-readonly={isEdit ? 'true' : undefined}
              required={!isEdit}
            />
            {!isEdit && emailError && (
              <small className="form-text text-danger">{emailError}</small>
            )}
            {!isEdit && isValidatingContact && (
              <small className="form-text text-muted">Validating contact...</small>
            )}
            {isEdit ? (
              <small className="form-text text-muted d-block">
                Mobile and email cannot be changed after registration.
              </small>
            ) : (
              <small className="form-text text-muted d-block">
                Admin registration — email is marked verified automatically.
              </small>
            )}
          </div>
        </div>
      </div>

      {!isEdit && (
        <div className="reg-form-section">
          <SectionTitle num={5} title="Account Password" />
          <div className="row">
            <div className="col-md-6 p-2">
              <label htmlFor="admin-password" className="form-label">
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  id="admin-password"
                  name="password"
                  value={formData.password}
                  onChange={onChange}
                  placeholder="Enter password (6-20 characters)"
                  minLength={6}
                  maxLength={20}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onToggleShowPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div className="col-md-6 p-2">
              <label htmlFor="admin-reenterPassword" className="form-label">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                  type={showReenterPassword ? 'text' : 'password'}
                  className={`form-control ${
                    formData.reenterPassword && !passwordsMatch
                      ? 'is-invalid'
                      : formData.reenterPassword && passwordsMatch
                        ? 'is-valid'
                        : ''
                  }`}
                  id="admin-reenterPassword"
                  name="reenterPassword"
                  value={formData.reenterPassword}
                  onChange={onChange}
                  placeholder="Re-enter password"
                  minLength={6}
                  maxLength={20}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onToggleShowReenterPassword}
                  tabIndex={-1}
                >
                  {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {formData.reenterPassword && !passwordsMatch && (
                <small className="form-text text-danger">Passwords do not match</small>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PatientRegistrationFormFields;
